import { Icons } from '@/assets/icons/icons';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { IconButton } from '@/components/ui/IconButton';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import type { Source } from './types';

const SOURCE_OPTIONS: ReadonlyArray<{ value: Source; label: string }> = [
  { value: 'node', label: 'Node' },
  { value: 'python', label: 'Python' },
  { value: 'program', label: 'Program' },
  { value: 'url', label: 'Local URL' },
];

export function SourceField({
  source,
  onSourceChange,
  pkg,
  onPkgChange,
  command,
  onCommandChange,
  onPickFile,
  url,
  onUrlChange,
  urlError,
}: {
  source: Source;
  onSourceChange: (v: Source) => void;
  pkg: string;
  onPkgChange: (v: string) => void;
  command: string;
  onCommandChange: (v: string) => void;
  onPickFile: () => void;
  url: string;
  onUrlChange: (v: string) => void;
  urlError: boolean;
}) {
  const inputClass =
    'min-w-0 flex-1 border-none bg-transparent px-2.5 font-mono text-xs text-foreground outline-none placeholder:text-foreground-muted';
  const currentLabel =
    SOURCE_OPTIONS.find((opt) => opt.value === source)?.label ?? 'Node';

  return (
    <YStack className="gap-2">
      <XStack
        className={cn(
          'items-stretch overflow-hidden rounded-lg border bg-surface',
          urlError ? 'border-danger-8' : 'border-border-strong'
        )}
      >
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="flex h-10 shrink-0 items-center gap-1 border-r border-border pl-2.5 pr-2 text-xs text-foreground outline-none hover:bg-surface-hover data-[state=open]:bg-surface-hover"
            >
              {currentLabel}
              <Icons.chevronDown className="size-3 text-foreground-muted" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            fit
            align="start"
            className="z-[200] min-w-[9rem]"
          >
            <DropdownMenu.RadioGroup
              value={source}
              onValueChange={(v) => onSourceChange(v as Source)}
            >
              {SOURCE_OPTIONS.map((opt) => (
                <DropdownMenu.RadioItem key={opt.value} value={opt.value}>
                  {opt.label}
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        {source === 'url' ? (
          <input
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            spellCheck={false}
            placeholder="http://localhost:3000/mcp"
            className={inputClass}
          />
        ) : source === 'program' ? (
          <>
            <input
              value={command}
              onChange={(e) => onCommandChange(e.target.value)}
              spellCheck={false}
              placeholder="/usr/local/bin/tableplus-mcp or docker"
              className={inputClass}
            />
            <div className="flex items-center border-l border-border px-1">
              <IconButton
                icon={Icons.file}
                variant="ghost"
                size="sm"
                tooltip="Pick a program"
                onClick={onPickFile}
              />
            </div>
          </>
        ) : (
          <input
            value={pkg}
            onChange={(e) => onPkgChange(e.target.value)}
            spellCheck={false}
            placeholder="@modelcontextprotocol/server-memory"
            className={inputClass}
          />
        )}
      </XStack>

      {source === 'url' ? (
        urlError ? (
          <p className="text-xs text-danger-11">
            Remote MCP servers are added under Integrations instead.
          </p>
        ) : (
          <p className="text-xs text-foreground-muted">
            Must be on <span className="font-mono">localhost</span> — remote
            servers live under Integrations.
          </p>
        )
      ) : source === 'program' ? (
        <p className="text-xs text-foreground-muted">
          Absolute path or a program name on your PATH. Runs directly — no
          shell.
        </p>
      ) : (
        <p className="text-xs text-foreground-muted">
          Pin a version for reproducible installs — e.g.{' '}
          <span className="font-mono">
            {source === 'python'
              ? 'mcp-server-fetch==1.2.3'
              : 'server-memory@2025.8.4'}
          </span>
        </p>
      )}
    </YStack>
  );
}
